import { InView } from 'react-intersection-observer';
import React, { useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { capitalize, flatten } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import CardMediaCarousel from 'dpl/breeder_search_results/components/CardMediaCarousel';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import {
  PuppyCardPropTypes,
  PuppyCardPropDefaultTypes
} from 'dpl/shared/utils/customPropTypes';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { getAnalyticsOrigin } from 'dpl/shared/utils/getAnalyticsOrigin';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  FOLLOWABLE_TYPES,
  FAVORITE_BUTTON_VARIANTS
} from 'dpl/favorites/utils/constants';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import Icon from 'dpl/common/components/Icon';
import { GENDERS } from 'dpl/shared/constants/breeder_app';
import { PUPPY_GENDERS } from 'dpl/puppy_search_results/utils/constants';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';

import CardFooter from './CardFooter';

const isLessThanMd = isLessThanBreakpoint('md');

const smartImageProps = {
  Tag: 'div',
  className: 'w-100 bg-center cover',
  crop: true,
  loadingClass: 'bg-light-gray',
  allowContextMenu: true,
  height: isLessThanMd ? 'calc(100vw - 48px)' : '335px'
};

function puppyName(puppy) {
  if (puppy.gender === GENDERS.MALE && puppy.name.startsWith('Boy ')) {
    return puppy.name;
  } else if (
    puppy.gender === GENDERS.FEMALE &&
    puppy.name.startsWith('Girl ')
  ) {
    return puppy.name;
  }
  return `${puppy.name}, ${capitalize(
    puppy.gender === GENDERS.MALE ? PUPPY_GENDERS.BOY : PUPPY_GENDERS.GIRL
  )}`;
}

export default function Card({
  puppy,
  analytics,
  className,
  style,
  trackInView,
  onClick,
  onFavoriteChange,
  view,
  bordered,
  ctaType
}) {
  const { locationTitle, breedId } = useSelector(({ server }) => server);
  const { isLoggedIn, isBuyer } = useCurrentUser();

  const showFavoriteButton = !isLoggedIn || isBuyer;

  const {
    id,
    name,
    reserved,
    profile_photo_id: profilePhotoId,
    square_profile_photo_url: profilePhotoUrl,
    distance_in_miles: distance,
    breeder_profile: breederProfile,
    litter_id: litterId,
    media_files: mediaFiles,
    approved_testimonial_count: testimonialsCount
  } = puppy;

  const {
    id: breederProfileId,
    url: breederProfileUrl,
    name: breederProfileProgramName,
    display_name: breederProfileDisplayName,
    delivery_methods: deliveryMethods,
    delivery_methods_count: deliveryMethodsCount,
    first_name: breederFirstName,
    preferred_qualification: preferredQualification,
    profile_photo_url_wo_missing: breederPhotoUrl
  } = breederProfile;

  const preferredQualificationStatus = preferredQualification?.status;
  const isPreferredBreeder = preferredQualificationStatus === STATUS.PREFERRED;

  const favoriteAnalytics = useMemo(
    () => ({
      ...analytics,
      page_number: analytics.page_number || null,
      position: analytics.position || null,
      breeder_profile_id: breederProfileId,
      breeder_name: breederProfileProgramName,
      dog_id: id,
      breed_id: Number(breedId) || null
    }),
    [id, analytics]
  );

  const computedAnalytics = useMemo(
    () => ({
      ...analytics,
      breeder_preferred_status: preferredQualificationStatus,
      breeder_profile_id: breederProfileId,
      view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
      miles_away: distance,
      dog_id: id,
      breed_id: breedId,
      transportation: flatten(Object.values(deliveryMethods || {})).map(
        method => method.name
      )
    }),
    [id, analytics]
  );

  const puppyMediaFiles = useMemo(() => {
    const pupMediaFiles = [...(mediaFiles || [])];
    if (profilePhotoId || pupMediaFiles.length === 0) {
      pupMediaFiles.unshift({
        id: profilePhotoId,
        url: profilePhotoUrl
      });
    }
    return pupMediaFiles;
  }, [mediaFiles, profilePhotoId]);

  const photosWithAlt = useMemo(() => {
    const images = puppyMediaFiles.map(photo => ({
      ...photo,
      alt: locationTitle ? `${name} in ${locationTitle}` : name
    }));

    return images;
  }, [locationTitle, name, puppyMediaFiles]);

  const userAvatarBreeder = {
    ...breederProfile,
    profile_photo_url: breederPhotoUrl
  };

  const handleClick = useCallback(() => {
    const { origin_view: originView } = getAnalyticsOrigin();

    setAnalyticsOrigin({
      originView,
      originViewFeature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
      originViewFeatureLocation: analytics?.view_feature_location
    });
    sendRudderstackEvent(
      ANALYTICS_EVENTS.CARD_CLICKED_BY_USER,
      computedAnalytics
    );
    onClick?.();
  }, [onClick, analytics, computedAnalytics]);

  return (
    <InView
      triggerOnce
      onChange={inView => {
        if (inView) {
          trackInView();
        }
      }}
      threshold={0.8}
      className="h-100"
    >
      <ClickableDiv
        className={classnames('PuppySearchResultsCard h-100 db', className, {
          'ba b--default br3': bordered
        })}
        style={style}
        onClick={({ currentTarget, target }) => {
          if (currentTarget === target || currentTarget.contains(target)) {
            handleClick();
            openPuppyPage({ puppyId: id });
          }
        }}
      >
        <div className="PuppySearchResultsCard__container h-100 flex flex-column justify-between">
          <div>
            <div>
              <div
                className={classnames(
                  'overflow-hidden w-100 h-100 br3 relative z-0',
                  { 'br--top': bordered }
                )}
              >
                <CardMediaCarousel
                  mediaFiles={photosWithAlt}
                  smartImageProps={smartImageProps}
                  isPuppyCard
                  lazyTriggerOnce={false}
                  lazyBuffer={window.outerHeight * 2}
                />
                <div className="white absolute w-100 top-0 right-0">
                  <div className="ph4 pv4 absolute right-0">
                    {showFavoriteButton && (
                      <FavoriteButton
                        analytics={favoriteAnalytics}
                        followableTypeId={id}
                        followableType={FOLLOWABLE_TYPES.DOG}
                        variant={FAVORITE_BUTTON_VARIANTS.TRANSPARENT_ICON_ONLY}
                        onChange={onFavoriteChange}
                        iconSize="24px"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className={classnames({ 'ph4 pb4': bordered })}>
                <div className="PuppySearchResultsItem__name flex items-center justify-between mt4 mb1 font-16-mobile f3-md">
                  <p className="fw-medium truncate font-20 font-serif">
                    {puppyName(puppy)}
                  </p>
                  {testimonialsCount > 0 && (
                    <div className="flex">
                      <Icon
                        name="fetch-star"
                        height="16px"
                        width="16px"
                        className="mr1 tertiary"
                      />
                      <p className="font-14">{testimonialsCount}</p>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex mr2 items-center">
                    <div className="relative">
                      <UserAvatar user={userAvatarBreeder} size="xs" />
                      {isPreferredBreeder && (
                        <PreferredBreederBadge
                          className="absolute right-0 bottom-0 nr1"
                          width="12px"
                          height="12px"
                          hideTooltip
                        />
                      )}
                    </div>
                    <p className="ml2 font-14 secondary truncate">
                      Raised by {breederProfileDisplayName}
                    </p>
                    <p className="font-14 secondary">
                      &nbsp;&bull; {puppy.ready_date_description}
                    </p>
                  </div>
                  <CardFooter
                    deliveryMethods={deliveryMethods}
                    breederProfileUrl={breederProfileUrl}
                    breederProfileId={breederProfileId}
                    snoozed={false}
                    deliveryMethodsCount={deliveryMethodsCount}
                    litterId={litterId}
                    puppyId={id}
                    analytics={computedAnalytics}
                    puppyName={name}
                    isReservedPuppy={reserved}
                    breederFirstName={breederFirstName}
                    view={view}
                    distance={distance}
                    ctaType={ctaType}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ClickableDiv>
    </InView>
  );
}

Card.propTypes = PuppyCardPropTypes;
Card.defaultProps = PuppyCardPropDefaultTypes;
